import React, { useState, useEffect } from 'react';
import { BookOpen, Save, ArrowLeft, Maximize2, Minimize2, X, User } from 'lucide-react';
import api from '../api';

const CreateDiplomaturaMenu = ({ onClose, editData }) => {
  const [formData, setFormData] = useState({ 
    nombre: editData ? editData.name : '', 
    municipio: editData ? editData.municipio : '',
    description: editData ? (editData.description || '') : '',
    coordinator: editData ? (editData.coordinator || '') : '',
    referentes: editData ? (editData.referentes_data ? editData.referentes_data.map(r => r.id) : []) : [],
    monthly_coordinator_rate: editData ? (editData.monthly_coordinator_rate || '') : '',
    hourly_teacher_rate: editData ? (editData.hourly_teacher_rate || '') : ''
  });
  const [coordinadores, setCoordinadores] = useState([]);
  const [referentes, setReferentes] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isEditing = !!editData;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('users/');
      // Filter for Coordinadores de Diplomatura (CD role)
      setCoordinadores(res.data.filter(u => u.role === 'CD'));
      // Filter for Referentes Municipales (RM role)
      setReferentes(res.data.filter(u => u.role === 'RM'));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      name: formData.nombre,
      municipio: formData.municipio,
      description: formData.description,
      coordinator: formData.coordinator || null,
      referentes_ids: formData.referentes,
      monthly_coordinator_rate: formData.monthly_coordinator_rate === '' ? 0 : formData.monthly_coordinator_rate,
      hourly_teacher_rate: formData.hourly_teacher_rate === '' ? 0 : formData.hourly_teacher_rate
    };

    try {
      if (isEditing) {
        await api.patch(`diplomas/${editData.id}/`, payload);
        alert(`Diplomatura actualizada exitosamente: ${formData.nombre}`);
      } else {
        await api.post('diplomas/', payload);
        alert(`Diplomatura creada exitosamente: ${formData.nombre} (${formData.municipio})`);
      }
      onClose();
    } catch (err) {
      console.error('Error submitting diplomatura:', err);
      alert('Error en el servidor al procesar la solicitud');
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className={`modal-dialog modal-dialog-centered ${isFullscreen ? 'modal-fullscreen' : ''}`}>
        <div className="modal-content glassmorphism">
          <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
            <h5 className="modal-title section-title fs-4 mb-0 d-flex align-items-center">
              <BookOpen size={24} className={`me-2 ${isEditing ? 'text-primary' : 'text-success'}`} /> {isEditing ? 'Editar Diplomatura' : 'Crear Diplomatura'}
            </h5>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <button
                type="button"
                className="btn-glass p-1 d-flex align-items-center justify-content-center"
                onClick={() => setIsFullscreen(f => !f)}
                title={isFullscreen ? 'Contraer' : 'Ampliar a pantalla completa'}
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                type="button"
                className="btn-close-premium"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="modal-body">
            <p className="text-muted mb-4">Ingrese los datos para la nueva diplomatura.</p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-3 text-start">
                  <label className="form-label text-muted">Nombre de la Diplomatura</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ej: Diplomatura en Inteligencia Artificial" 
                    value={formData.nombre} 
                    onChange={e => setFormData({...formData, nombre: e.target.value})} 
                    required 
                  />
              </div>
              <div className="mb-3 text-start">
                  <label className="form-label text-muted">Municipio</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ej: San Miguel" 
                    value={formData.municipio} 
                    onChange={e => setFormData({...formData, municipio: e.target.value})} 
                    required 
                  />
              </div>
              <div className="mb-4 text-start">
                  <label className="form-label text-muted">Descripción (Opcional)</label>
                  <textarea 
                    className="form-control" 
                    placeholder="Breve descripción de la diplomatura..." 
                    value={formData.description} 
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    rows="2"
                  />
              </div>
              <div className="mb-4 text-start">
                  <label className="form-label text-muted">Coordinador de Diplomatura</label>
                  <select 
                    className="form-select" 
                    value={formData.coordinator} 
                    onChange={e => setFormData({...formData, coordinator: e.target.value})}
                  >
                    <option value="">Seleccione un coordinador...</option>
                    {coordinadores.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.first_name || c.last_name ? `${c.first_name} ${c.last_name} (${c.email})` : c.email}
                      </option>
                    ))}
                  </select>
              </div>
              <div className="mb-4 text-start">
                  <label className="form-label text-muted">Referentes Municipales</label>
                  <div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {referentes.length > 0 ? referentes.map(r => (
                      <div key={r.id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`ref-${r.id}`}
                          checked={formData.referentes.includes(r.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setFormData({...formData, referentes: [...formData.referentes, r.id]});
                            } else {
                              setFormData({...formData, referentes: formData.referentes.filter(id => id !== r.id)});
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor={`ref-${r.id}`}>
                          {r.first_name || r.last_name ? `${r.first_name} ${r.last_name} (${r.email})` : r.email}
                        </label>
                      </div>
                    )) : (
                      <span className="text-muted small">No hay referentes disponibles</span>
                    )}
                  </div>
              </div>
              <div className="row mb-4">
                  <div className="col-md-6 text-start">
                      <label className="form-label text-muted">Honorarios Coordinador (Mensual)</label>
                      <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">$</span>
                          <input 
                            type="number" 
                            className="form-control border-start-0" 
                            placeholder="0.00" 
                            value={formData.monthly_coordinator_rate} 
                            onChange={e => setFormData({...formData, monthly_coordinator_rate: e.target.value})}
                          />
                      </div>
                  </div>
                  <div className="col-md-6 text-start">
                      <label className="form-label text-muted">Honorarios Docente (Hora)</label>
                      <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">$</span>
                          <input 
                            type="number" 
                            className="form-control border-start-0" 
                            placeholder="0.00" 
                            value={formData.hourly_teacher_rate} 
                            onChange={e => setFormData({...formData, hourly_teacher_rate: e.target.value})}
                          />
                      </div>
                  </div>
              </div>
              <div className="d-flex justify-content-between">
                  <button type="button" className="btn btn-secondary me-2" onClick={onClose}>
                      <ArrowLeft size={16} className="me-1"/> Cancelar
                  </button>
                  <button type="submit" className="btn btn-success flex-grow-1">
                      <Save size={16} className="me-1"/> Guardar
                  </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDiplomaturaMenu;
